import {useMemo, useState} from "react";
import {BaseAlert, BaseBadge, BaseBreadcrumb, BaseButton, BaseCard} from "@/components";
import type {Transaction, TransactionTypeFilter} from "./transactions.types";
import {useTransactions} from "./useTransactions";

const FILTER_TABS: ReadonlyArray<{value: TransactionTypeFilter; label: string}> = [
  {value: "deposit", label: "Depósitos"},
  {value: "withdrawals", label: "Retiros"},
  {value: "transfers", label: "Transferencias"},
  {value: "bet", label: "Apuestas"},
  {value: "bonus", label: "Bonos"},
];

const PAGE_SIZE = 10;

const formatDate = (iso: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("es-VE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const amountColor = (amount: number): string =>
  amount < 0 ? "text-[var(--color-danger)]" : "text-[var(--color-success)]";

const resolveTitle = (tx: Transaction): string => {
  if (tx.counterpartyName) return tx.counterpartyName;
  return tx.transactionType.replace(/-/g, " ");
};

const TransactionRow = ({tx}: {tx: Transaction}) => (
  <BaseCard
    variant="surface"
    padding="md"
    className="flex items-center justify-between gap-4"
  >
    <div className="min-w-0 flex-1">
      <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)] capitalize">
        {resolveTitle(tx)}
      </p>
      <p className="text-[var(--font-body)] text-[var(--color-text-tertiary)]">{formatDate(tx.createdAt)}</p>
    </div>
    <div className="flex flex-col items-end gap-1">
      <span className={`text-[var(--font-h3)] font-bold ${amountColor(tx.amount)}`}>
        {tx.formattedAmount || tx.amount.toFixed(2)}
      </span>
      {tx.currency ? (
        <BaseBadge
          variant="neutral"
          size="sm"
        >
          {tx.currency}
        </BaseBadge>
      ) : null}
    </div>
  </BaseCard>
);

export interface TransactionsPageProps {
  initialType?: TransactionTypeFilter;
}

const TransactionsPage = ({initialType = "deposit"}: TransactionsPageProps) => {
  const [type, setType] = useState<TransactionTypeFilter>(initialType);
  const [page, setPage] = useState(1);

  const {data, isLoading, isFetching, isError, error, refetch} = useTransactions({
    type,
    page,
    limit: PAGE_SIZE,
    sort: "desc",
  });

  const totalPages = useMemo(() => {
    if (!data || data.total === 0) return 1;
    return Math.max(1, Math.ceil(data.total / data.limit));
  }, [data]);

  const handleTypeChange = (next: TransactionTypeFilter) => {
    setType(next);
    setPage(1);
  };

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8">
      <BaseBreadcrumb items={[{label: "Movimientos"}]} />
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Historial de movimientos</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Consulta depósitos, retiros, transferencias, apuestas y bonos.
        </p>
      </header>

      <nav
        className="flex flex-wrap gap-2"
        aria-label="Filtrar movimientos"
      >
        {FILTER_TABS.map(tab => (
          <BaseButton
            key={tab.value}
            variant={type === tab.value ? "primary" : "ghost"}
            size="sm"
            onClick={() => handleTypeChange(tab.value)}
            aria-pressed={type === tab.value}
          >
            {tab.label}
          </BaseButton>
        ))}
      </nav>

      {isLoading ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
        </BaseCard>
      ) : isError ? (
        <BaseAlert
          variant="danger"
          title="No pudimos cargar tus movimientos"
        >
          <p>{error?.message ?? "Inténtalo nuevamente."}</p>
          <BaseButton
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
          >
            Reintentar
          </BaseButton>
        </BaseAlert>
      ) : !data || data.items.length === 0 ? (
        <BaseCard
          variant="surface"
          padding="lg"
        >
          <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
            Sin movimientos para mostrar.
          </p>
        </BaseCard>
      ) : (
        <ul className="flex flex-col gap-3">
          {data.items.map(tx => (
            <li key={tx.id}>
              <TransactionRow tx={tx} />
            </li>
          ))}
        </ul>
      )}

      {data && data.items.length > 0 ? (
        <footer className="flex items-center justify-between gap-3">
          <BaseButton
            variant="ghost"
            size="sm"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage(prev => Math.max(1, prev - 1))}
          >
            ← Anterior
          </BaseButton>
          <span className="text-[var(--font-body)] text-[var(--color-text-tertiary)]">
            Página {page} de {totalPages}
          </span>
          <BaseButton
            variant="ghost"
            size="sm"
            disabled={page >= totalPages || isFetching}
            onClick={() => setPage(prev => prev + 1)}
          >
            Siguiente →
          </BaseButton>
        </footer>
      ) : null}
    </section>
  );
};

export default TransactionsPage;
